// Generate an image and get it back as a data URL, ready to store.
//
// The Image Service is asynchronous: the POST enqueues the job and returns
// immediately, a worker generates the image, and the result is only reachable
// once the row reaches `completed`. So every caller has to submit, poll, and
// convert — this is that, in one place, because it was already written twice.
//
// Coins: charged by the backend, pre-checked at submit and deducted when the
// worker finishes. An insufficient balance fails the submit before any image
// exists (the error message comes back as `detail.message`).

import ImageService from './image.service';

/** Fetch the presigned URL and inline it, since presigned URLs expire in 24h. */
export const imageUrlToDataUrl = async (imageUrl) => {
  const response = await fetch(imageUrl);
  if (!response.ok) throw new Error('Failed to fetch the generated image');

  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read the generated image'));
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

/** Poll a submitted image until it is `completed` (or fails / times out). */
export const pollImageUntilReady = async (
  accountId,
  imageId,
  { intervalMs = 2500, timeoutMs = 90000 } = {}
) => {
  const start = Date.now();
  while (Date.now() - start <= timeoutMs) {
    const res = await ImageService.getImage({ accountId, imageId });
    if (res.success) {
      const status = res.data?.status;
      if (status === 'completed') {
        const url = res.data?.url;
        if (!url) throw new Error('The image completed but returned no URL.');
        return url;
      }
      if (status === 'failed') {
        throw new Error(res.data?.errorMessage || 'Image generation failed.');
      }
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error('Generation took too long. Try again.');
};

/**
 * Submit a prompt, wait for the worker, and return the image as a data URL.
 *
 * `projectId` is optional (zenitailab !859): an avatar or a profile picture
 * belongs to no project — a public avatar has none at all — and billing is
 * metered per account. Passing `projects[0].id` to satisfy the API, which is
 * what this used to do, filed the image under whichever project sorted first.
 */
export const generateImageDataUrl = async ({ accountId, projectId, prompt }) => {
  if (!accountId) throw new Error('No active account.');
  if (!prompt?.trim()) throw new Error('Describe the image you want.');

  const submit = await ImageService.generateImage({ accountId, projectId, prompt });
  if (!submit.success) {
    throw new Error(submit.error?.message || 'Could not start the generation.');
  }

  const imageId = submit.data?.imageIds?.[0];
  if (!imageId) throw new Error('The service returned no image id.');

  const url = await pollImageUntilReady(accountId, imageId);
  return imageUrlToDataUrl(url);
};
